// ----------------------------------------------------------------------------
// randfxr — Random Sound FX Generator (based on jsfxr + extended FX chain)
// ----------------------------------------------------------------------------
// One button. Generates 25 completely random sounds with random FX.
// Cards stack in a grid (latest on top). Drag cards to bucket.
// ----------------------------------------------------------------------------

const { qs, qsa, alertmessage } = require('../../libs/qs');
const { renderWaveform } = require('@azopsoft/shared/ui');
const audioBufferToWav = require('audiobuffer-to-wav');
const initLegacyBucket = require('./legacy-bucket-integration.js');
const jsfxr = require('./lib/sfxr.js');

const { Params, sfxr } = jsfxr;
const { SQUARE, SAWTOOTH, SINE, NOISE } = jsfxr.waveforms;

// ---- Theme ----
let currentTheme;
try { const s = localStorage.getItem('currentTheme'); if (s) currentTheme = JSON.parse(s); } catch (_) {}
if (!currentTheme) currentTheme = window.Theme;
const themeColors = Object.values(currentTheme.data);
window.themeColors = themeColors;
window.currentTheme = currentTheme;

// ---- AudioContext ----
let audioContext = null;
function getAudioContext() {
    if (!audioContext) {
        audioContext = new AudioContext();
        window.audioContext = audioContext;
        window.audioCtx = audioContext;
    }
    if (audioContext.state === 'suspended') audioContext.resume().catch(() => {});
    return audioContext;
}
document.addEventListener('click', () => getAudioContext(), { once: true, capture: true });

// ---- State ----
const store = {
    cards: [],
    currentSource: null,
    playingCardId: null,
    playStartTime: 0,
    playOffset: 0,
    playBuffer: null,
    playCanvas: null,
    rafId: null,
    cardCounter: 0,
};

// ---- All presets for random selection ----
const PRESETS = [
    'pickupCoin', 'laserShoot', 'explosion', 'powerUp',
    'hitHurt', 'jump', 'blipSelect', 'synth', 'tone', 'click', 'random',
];

// ---- Musical notes (frequencies) for random pitch selection ----
// Restricted to a musically useful range: A2 (110Hz) to E5 (659Hz)
// Too low = muddy rumble, too high = piercing squeak
const NOTE_FREQS = [
    // A2 to A4 (most useful range for SFX)
    110.00, 116.54, 123.47,
    130.81, 138.59, 146.83, 155.56, 164.81, 174.61, 185.00, 196.00, 207.65, 220.00, 233.08, 246.94,
    261.63, 277.18, 293.66, 311.13, 329.63, 349.23, 369.99, 392.00, 415.30, 440.00, 466.16, 493.88,
    // up to E5
    523.25, 554.37, 587.33, 622.25, 659.25,
];

const NOTE_NAMES = ['C','C#','D','D#','E','F','F#','G','G#','A','A#','B'];

// ---- Scales for pitch selection (weighted toward more musical scales) ----
const SCALES = {
    pentatonic: [0, 2, 4, 7, 9],    // most musical, no dissonance
    major: [0, 2, 4, 5, 7, 9, 11],
    minor: [0, 2, 3, 5, 7, 8, 10],
    blues: [0, 3, 5, 6, 7, 10],
    dorian: [0, 2, 3, 5, 7, 9, 10],
    mixolydian: [0, 2, 4, 5, 7, 9, 10],
};

// Weighted scale selection — pentatonic and major more likely
const SCALE_WEIGHTS = ['pentatonic','pentatonic','pentatonic','major','major','minor','minor','blues','dorian','mixolydian'];

// Convert a target frequency to sfxr p_base_freq
function freqToBaseFreq(freq, sampleRate) {
    return Math.sqrt(Math.max(0.001, freq * 100 / sampleRate));
}

// Convert sfxr p_base_freq back to frequency
function baseFreqToFreq(baseFreq, sampleRate) {
    const period = 100 / (baseFreq * baseFreq + 0.001);
    return sampleRate / period;
}

function randomNote() {
    const scaleName = SCALE_WEIGHTS[Math.floor(Math.random() * SCALE_WEIGHTS.length)];
    const scale = SCALES[scaleName];
    const octave = Math.floor(Math.random() * 3); // 0-2 (A2-E5 range in our table)
    const noteIdx = scale[Math.floor(Math.random() * scale.length)];
    const semitone = octave * 12 + noteIdx;
    const freq = NOTE_FREQS[Math.min(semitone, NOTE_FREQS.length - 1)];
    const noteName = NOTE_NAMES[noteIdx % 12] + (2 + octave);
    return { freq, noteName };
}

// ============================================================================
// RANDOM GENERATION — random params, random FX, random everything
// With musicality rules and quality guards
// ============================================================================

function randomParams() {
    const presetName = PRESETS[Math.floor(Math.random() * PRESETS.length)];
    const p = new Params();
    p.sound_vol = 0.5 + Math.random() * 0.4; // 0.5-0.9, never too quiet
    p.sample_rate = 44100; // keep full quality, lo-fi comes from FX
    p.sample_size = 16;

    if (typeof p[presetName] === 'function') {
        p[presetName]();
    } else {
        p.random();
    }

    // Moderate mutations — enough variety, not so much it breaks musicality
    p.mutate();
    if (Math.random() < 0.5) p.mutate();

    // --- PITCH: snap to musical note 70% of the time ---
    let noteName = null;
    if (Math.random() < 0.7) {
        const note = randomNote();
        p.p_base_freq = freqToBaseFreq(note.freq, p.sample_rate);
        noteName = note.noteName;
        // Snap freq limit to a nearby note (within an octave)
        if (p.p_freq_limit > 0 && Math.random() < 0.5) {
            const note2 = randomNote();
            p.p_freq_limit = freqToBaseFreq(note2.freq, p.sample_rate);
        }
    }

    // --- PITCH GUARD: clamp base freq to audible musical range ---
    // Too low = inaudible rumble, too high = piercing squeak
    // Valid freq range: 80Hz - 2000Hz
    const minBaseFreq = freqToBaseFreq(80, p.sample_rate);
    const maxBaseFreq = freqToBaseFreq(2000, p.sample_rate);
    if (p.p_base_freq < minBaseFreq) p.p_base_freq = minBaseFreq;
    if (p.p_base_freq > maxBaseFreq) p.p_base_freq = maxBaseFreq;
    if (p.p_freq_limit > 0) {
        if (p.p_freq_limit < minBaseFreq) p.p_freq_limit = minBaseFreq;
        if (p.p_freq_limit > maxBaseFreq) p.p_freq_limit = maxBaseFreq;
    }

    // --- PITCH RAMP GUARD: prevent extreme slides that go out of range ---
    // Limit freq_ramp to moderate values so the sound doesn't slide to inaudible
    if (Math.abs(p.p_freq_ramp) > 0.6) p.p_freq_ramp *= 0.5;
    if (Math.abs(p.p_freq_dramp) > 0.5) p.p_freq_dramp *= 0.5;

    // --- ENVELOPE: random duration 2 to 20 seconds ---
    // sfxr envelope alone maxes out around ~2.5s. For longer sounds we use
    // repeat + a post-generation loop/extend step in the FX chain.
    const targetDuration = 2 + Math.random() * 18; // 2s to 20s
    const targetSamples = Math.floor(targetDuration * p.sample_rate);

    // Ensure minimum audible duration
    const envTotal = p.p_env_attack + p.p_env_sustain + p.p_env_decay;
    if (envTotal < 0.05) {
        p.p_env_sustain = Math.max(p.p_env_sustain, 0.05);
        p.p_env_decay = Math.max(p.p_env_decay, 0.05);
    }

    // For long sounds, use repeat to loop the base sound
    if (targetDuration > 5) {
        // Set envelope to a medium length, then use repeat to extend
        p.p_env_sustain = 0.3 + Math.random() * 0.5;
        p.p_env_decay = 0.2 + Math.random() * 0.4;
        // Repeat speed controls how often the sound restarts
        p.p_repeat_speed = Math.max(0.01, 0.8 - (targetDuration / 20) * 0.6);
    } else {
        // 2-5s sounds — extend the envelope more
        p.p_env_sustain = 0.4 + Math.random() * 0.5;
        p.p_env_decay = 0.3 + Math.random() * 0.4;
    }

    // --- MUSICALITY: arpeggio should use musical intervals ---
    if (Math.random() < 0.35) {
        p.p_arp_speed = 0.3 + Math.random() * 0.6;
        // Snap arp_mod to musical intervals (fifths, octaves, thirds)
        const musicalArp = [0.137, 0.268, 0.316, 0.435, 0.585, 0.745];
        p.p_arp_mod = musicalArp[Math.floor(Math.random() * musicalArp.length)];
        if (Math.random() < 0.3) p.p_arp_mod = -p.p_arp_mod; // sometimes down
    }

    // Random wave type — prefer square and sawtooth for richer harmonics
    if (Math.random() < 0.25) {
        const waveChoice = Math.random();
        if (waveChoice < 0.4) p.wave_type = 0; // square
        else if (waveChoice < 0.7) p.wave_type = 1; // sawtooth
        else if (waveChoice < 0.9) p.wave_type = 2; // sine
        else p.wave_type = 3; // noise
    }

    // --- MUSICALITY: vibrato should be subtle, not extreme ---
    if (Math.random() < 0.35) {
        p.p_vib_strength = Math.random() * 0.4; // subtle, not extreme
        p.p_vib_speed = 0.3 + Math.random() * 0.5;
    }

    // Random flanger (subtle)
    if (Math.random() < 0.25) {
        p.p_pha_offset = (Math.random() * 2 - 1) * 0.5;
        p.p_pha_ramp = (Math.random() * 2 - 1) * 0.3;
    }

    // Random filter sweeps (musical range)
    if (Math.random() < 0.35) {
        p.p_lpf_freq = 0.3 + Math.random() * 0.6;
        p.p_lpf_ramp = (Math.random() * 2 - 1) * 0.3;
        p.p_lpf_resonance = (Math.random() * 2 - 1) * 0.5;
    }
    if (Math.random() < 0.25) {
        p.p_hpf_freq = Math.random() * 0.25; // gentle, don't thin out too much
        p.p_hpf_ramp = (Math.random() * 2 - 1) * 0.2;
    }

    // Random repeat (not too fast)
    if (Math.random() < 0.15) {
        p.p_repeat_speed = 0.2 + Math.random() * 0.4;
    }

    return { params: p, presetName, noteName, targetDuration };
}

function randomFX() {
    const r = Math.random;
    // Each FX generates its own random params fresh, so repeats produce different results
    const numFX = Math.floor(r() * 11); // 0 to 10 FX
    const chain = [];
    for (let i = 0; i < numFX; i++) {
        chain.push(randomFXStep());
    }
    return { chain, normalize: true, gain: 0.8 + r() * 0.3 };
}

// All available FX types — each generates random params for one instance
const FX_TYPES = [
    'distortion', 'bitcrush', 'waveshape', 'downsampling',
    'lowpass', 'highpass', 'combFilter',
    'chorus', 'tremolo', 'ringMod',
    'reverb', 'delay',
    'pitchShift', 'stereoPan', 'stereoWidth', 'autopan',
    'fadeOut', 'fadeIn', 'reverse',
    'stutter', 'tapeWobble', 'freqShift', 'sampleHold',
    'halfSpeed', 'doubleSpeed',
    'granular', 'paulStretch',
];

function randomFXStep() {
    const r = Math.random;
    const type = FX_TYPES[Math.floor(r() * FX_TYPES.length)];
    const step = { type };
    switch (type) {
        case 'distortion':   step.amount = r() * 0.6; break;
        case 'bitcrush':     step.amount = r() * 0.5; break;
        case 'waveshape':    step.amount = r() * 0.6; break;
        case 'downsampling': step.factor = Math.floor(r() * 4) + 2; break;
        case 'lowpass':      step.cutoff = 0.3 + r() * 0.6; break;
        case 'highpass':     step.cutoff = r() * 0.3; break;
        case 'combFilter':   step.wet = r() * 0.4; step.delay = Math.floor(r() * 100) + 20; step.feedback = 0.3 + r() * 0.4; break;
        case 'chorus':       step.wet = r() * 0.6; step.rate = 0.5 + r() * 3; step.depth = 0.002 + r() * 0.008; break;
        case 'tremolo':      step.wet = r() * 0.5; step.rate = 3 + r() * 10; break;
        case 'ringMod':      step.wet = r() * 0.4; step.freq = 200 + r() * 800; break;
        case 'reverb':
            step.wet = 0.15 + r() * 0.55;
            step.size = 0.1 + r() * 0.8;
            step.reverbType = ['hall','plate','spring','room','shimmer'][Math.floor(r() * 5)];
            step.damp = r() * 0.8;
            step.predelay = r() * 0.05;
            break;
        case 'delay':
            step.wet = 0.15 + r() * 0.5;
            step.time = 0.05 + r() * 0.5;
            step.feedback = 0.2 + r() * 0.5;
            step.delayType = ['tape','pingpong','multitap','slapback'][Math.floor(r() * 4)];
            step.tone = r() < 0.4 ? 0.2 + r() * 0.5 : 1;
            break;
        case 'pitchShift':   step.semitones = Math.floor(r() * 15) - 7; break;
        case 'stereoPan':    step.pan = r() * 2 - 1; break;
        case 'stereoWidth':  step.width = r() * 0.6; break;
        case 'autopan':      step.amount = r() * 0.7; step.rate = 0.5 + r() * 4; break;
        case 'fadeOut':      step.amount = r() * 0.4; break;
        case 'fadeIn':       step.amount = r() * 0.2; break;
        case 'reverse':      step.active = true; break;
        case 'stutter':      step.repeats = Math.floor(r() * 8) + 2; step.size = Math.floor(r() * 2000) + 200; break;
        case 'tapeWobble':   step.amount = r() * 0.5; step.rate = 1 + r() * 5; break;
        case 'freqShift':    step.shift = r() * 400 - 200; break;
        case 'sampleHold':   step.hold = Math.floor(r() * 50) + 5; break;
        case 'halfSpeed':    step.active = true; break;
        case 'doubleSpeed':  step.active = true; break;
        case 'granular':
            step.wet = 0.3 + r() * 0.6;
            step.grainSize = 0.02 + r() * 0.08;
            step.density = 10 + r() * 50;
            step.pitch = r() < 0.4 ? (r() * 2 - 1) * 0.5 : 0;
            step.spread = r() * 0.8;
            step.pan = r() < 0.5;
            step.grainReverse = r() < 0.3;
            break;
        case 'paulStretch':  step.factor = 2 + r() * 8; break;
    }
    return step;
}

// ============================================================================
// WEB WORKER — off-main-thread audio generation
// ============================================================================

let randfxrWorker = null;
function getWorker() {
    if (randfxrWorker) return randfxrWorker;
    const workerSource = typeof __RANDFXR_WORKER_SOURCE__ !== 'undefined'
        ? __RANDFXR_WORKER_SOURCE__
        : '';
    if (!workerSource) { return null; }
    const blob = new Blob([workerSource], { type: 'application/javascript' });
    const url = URL.createObjectURL(blob);
    randfxrWorker = new Worker(url);
    return randfxrWorker;
}

function destroyWorker() {
    if (randfxrWorker) {
        try { randfxrWorker.terminate(); } catch (_) {}
        randfxrWorker = null;
    }
}

const WORKER_TIMEOUT_MS = 8000; // 8s per attempt — generous, covers long paulStretch/reverb
const MAX_WORKER_RETRIES = 3;

function generateViaWorker(onProgress) {
    return new Promise((resolve, reject) => {
        const worker = getWorker();
        if (!worker) { reject(new Error('Worker not available')); return; }
        let settled = false;
        let timer = setTimeout(() => {
            if (settled) return;
            settled = true;
            worker.removeEventListener('message', handler);
            // Worker is stuck — kill and recreate it
            destroyWorker();
            reject(new Error('Worker timeout'));
        }, WORKER_TIMEOUT_MS);
        const handler = (e) => {
            if (e.data.type === 'progress') {
                if (!settled && onProgress) onProgress(e.data);
            } else if (e.data.type === 'done') {
                if (settled) return;
                settled = true;
                clearTimeout(timer);
                worker.removeEventListener('message', handler);
                const { channels, sampleRate, numChannels, presetName, noteName } = e.data;
                const ctx = getAudioContext();
                const audioBuffer = ctx.createBuffer(numChannels, channels[0].length, sampleRate);
                for (let ch = 0; ch < numChannels; ch++) {
                    audioBuffer.getChannelData(ch).set(channels[ch]);
                }
                resolve({ audioBuffer, presetName, noteName });
            } else if (e.data.type === 'error') {
                if (settled) return;
                settled = true;
                clearTimeout(timer);
                worker.removeEventListener('message', handler);
                reject(new Error(e.data.message));
            }
        };
        worker.addEventListener('message', handler);
        worker.postMessage({ type: 'generate' });
    });
}

async function generateWithRetry(onProgress) {
    let lastErr = null;
    for (let attempt = 0; attempt < MAX_WORKER_RETRIES; attempt++) {
        try {
            return await generateViaWorker(onProgress);
        } catch (err) {
            lastErr = err;
            // If worker timed out, it's already been destroyed and will be recreated
            // on the next attempt. If it was a normal error, also recreate to be safe.
            destroyWorker();
        }
    }
    throw lastErr || new Error('All retries failed');
}

// ============================================================================
// FX CHAIN — applies a random list of FX steps in order
// Each step is { type, ...params } from randomFXStep()
// ============================================================================

function applyFXChain(audioBuffer, fx) {
    let data = audioBuffer.getChannelData(0).slice();
    const sr = audioBuffer.sampleRate;
    const chain = fx.chain || [];

    for (const step of chain) {
        if (!data || data.length === 0) break;
        data = applyFXStep(data, sr, step);
    }

    if (!data || data.length === 0) return null;

    // Gain
    if (fx.gain && fx.gain !== 1) {
        for (let i = 0; i < data.length; i++) data[i] *= fx.gain;
    }

    // Sanitize
    data = sanitizeAudio(data, sr, fx.normalize);

    // Check for stereo FX in the chain — apply them post-chain
    const stereoSteps = chain.filter((s) => s.type === 'stereoPan' || s.type === 'stereoWidth' || s.type === 'autopan');
    if (stereoSteps.length > 0) {
        const ctx = getAudioContext();
        const buf = ctx.createBuffer(2, data.length, sr);
        const left = buf.getChannelData(0);
        const right = buf.getChannelData(1);
        for (let i = 0; i < data.length; i++) { left[i] = data[i]; right[i] = data[i]; }
        for (const step of stereoSteps) {
            if (step.type === 'stereoPan') {
                const pan = step.pan;
                for (let i = 0; i < data.length; i++) {
                    const leftGain = pan <= 0 ? 1 : 1 - pan;
                    const rightGain = pan >= 0 ? 1 : 1 + pan;
                    left[i] *= leftGain;
                    right[i] *= rightGain;
                }
            } else if (step.type === 'stereoWidth') {
                const haasDelay = Math.floor(sr * 0.015 * step.width);
                const delayedRight = new Float32Array(data.length);
                for (let i = 0; i < data.length; i++) {
                    delayedRight[i] = i >= haasDelay ? right[i - haasDelay] : 0;
                }
                right.set(delayedRight);
            } else if (step.type === 'autopan') {
                for (let i = 0; i < data.length; i++) {
                    const p = Math.sin(2 * Math.PI * step.rate * i / sr) * step.amount;
                    const leftGain = p <= 0 ? 1 : 1 - p;
                    const rightGain = p >= 0 ? 1 : 1 + p;
                    left[i] *= leftGain;
                    right[i] *= rightGain;
                }
            }
        }
        return buf;
    }

    const ctx = getAudioContext();
    const buf = ctx.createBuffer(1, data.length, sr);
    buf.getChannelData(0).set(data);
    return buf;
}

function applyFXStep(data, sr, step) {
    switch (step.type) {
        case 'pitchShift': {
            if (step.semitones === 0) return data;
            const ratio = Math.pow(2, step.semitones / 12);
            const newLen = Math.floor(data.length / ratio);
            const newData = new Float32Array(newLen);
            for (let i = 0; i < newLen; i++) {
                const srcIdx = i * ratio;
                const i0 = Math.floor(srcIdx);
                const i1 = Math.min(i0 + 1, data.length - 1);
                const frac = srcIdx - i0;
                newData[i] = data[i0] * (1 - frac) + data[i1] * frac;
            }
            return newData;
        }
        case 'downsampling': {
            const factor = step.factor;
            const newData = new Float32Array(data.length);
            for (let i = 0; i < data.length; i++) {
                newData[i] = data[Math.floor(i / factor) * factor];
            }
            return newData;
        }
        case 'bitcrush': {
            const bits = Math.max(1, Math.floor(16 - step.amount * 15));
            const levels = Math.pow(2, bits);
            const stepSize = 2 / levels;
            for (let i = 0; i < data.length; i++) data[i] = Math.round(data[i] / stepSize) * stepSize;
            return data;
        }
        case 'waveshape': {
            const fold = 1 + step.amount * 5;
            for (let i = 0; i < data.length; i++) {
                let x = data[i] * fold;
                while (x > 1 || x < -1) {
                    if (x > 1) x = 2 - x;
                    if (x < -1) x = -2 - x;
                }
                data[i] = x;
            }
            return data;
        }
        case 'distortion': {
            const drive = 1 + step.amount * 50;
            for (let i = 0; i < data.length; i++) data[i] = Math.tanh(data[i] * drive);
            return data;
        }
        case 'ringMod': {
            const modFreq = step.freq;
            const wet = step.wet;
            for (let i = 0; i < data.length; i++) {
                const mod = Math.sin(2 * Math.PI * modFreq * i / sr);
                data[i] = data[i] * (1 - wet) + data[i] * mod * wet;
            }
            return data;
        }
        case 'lowpass': {
            const cutoff = Math.max(20, step.cutoff * sr / 2);
            const alpha = 2 * Math.PI * cutoff / sr;
            const a = alpha / (alpha + 1);
            let prev = 0;
            for (let i = 0; i < data.length; i++) { prev = prev + a * (data[i] - prev); data[i] = prev; }
            return data;
        }
        case 'highpass': {
            const cutoff = Math.max(20, step.cutoff * sr / 2);
            const alpha = 2 * Math.PI * cutoff / sr;
            let prevIn = 0, prevOut = 0;
            for (let i = 0; i < data.length; i++) {
                const out = alpha * (prevOut + data[i] - prevIn);
                prevIn = data[i]; prevOut = out; data[i] = out;
            }
            return data;
        }
        case 'combFilter': {
            const delayS = step.delay;
            const fb = step.feedback;
            const wet = step.wet;
            const buffer = new Float32Array(data.length);
            for (let i = 0; i < data.length; i++) {
                const delayed = i >= delayS ? buffer[i - delayS] : 0;
                buffer[i] = data[i] + delayed * fb;
                data[i] = data[i] * (1 - wet) + buffer[i] * wet;
            }
            return data;
        }
        case 'chorus': {
            const maxDelay = Math.floor(sr * step.depth);
            const output = new Float32Array(data.length);
            const wet = step.wet;
            for (let i = 0; i < data.length; i++) {
                const modDelay = maxDelay * (0.5 + 0.5 * Math.sin(2 * Math.PI * step.rate * i / sr));
                const readIdx = i - modDelay;
                if (readIdx >= 0 && readIdx < data.length) {
                    const i0 = Math.floor(readIdx);
                    const i1 = Math.min(i0 + 1, data.length - 1);
                    const frac = readIdx - i0;
                    output[i] = data[i] * (1 - wet) + (data[i0] * (1 - frac) + data[i1] * frac) * wet;
                } else {
                    output[i] = data[i] * (1 - wet);
                }
            }
            return output;
        }
        case 'tremolo': {
            const wet = step.wet;
            for (let i = 0; i < data.length; i++) {
                const amp = 1 - wet * (0.5 + 0.5 * Math.sin(2 * Math.PI * step.rate * i / sr));
                data[i] *= amp;
            }
            return data;
        }
        case 'delay': {
            const delaySamples = Math.floor(step.time * sr);
            const feedback = step.feedback * 0.7;
            const wet = step.wet;
            const dtype = step.delayType;
            if (dtype === 'slapback') {
                const slapDelay = Math.min(delaySamples, Math.floor(sr * 0.12));
                const output = new Float32Array(data.length + slapDelay);
                output.set(data);
                for (let i = 0; i < data.length; i++) {
                    if (i + slapDelay < output.length) output[i + slapDelay] += data[i] * wet * 0.8;
                }
                return output;
            } else if (dtype === 'multitap') {
                const taps = [0.33, 0.5, 0.75, 1.0];
                const maxTap = Math.floor(delaySamples * taps[taps.length - 1]);
                const output = new Float32Array(data.length + maxTap);
                output.set(data);
                for (let i = 0; i < data.length; i++) {
                    taps.forEach((tap, idx) => {
                        const tapDelay = Math.floor(delaySamples * tap);
                        const tapGain = wet * Math.pow(0.6, idx);
                        if (i + tapDelay < output.length) output[i + tapDelay] += data[i] * tapGain;
                    });
                }
                return output;
            } else if (dtype === 'pingpong') {
                const output = new Float32Array(data.length + delaySamples * 6);
                output.set(data);
                for (let i = 0; i < data.length; i++) {
                    let fb = feedback;
                    let offset = 0;
                    for (let tap = 0; tap < 6; tap++) {
                        offset += delaySamples;
                        if (i + offset < output.length) {
                            output[i + offset] += data[i] * fb * wet * (tap % 2 === 0 ? 0.7 : 1.0);
                        }
                        fb *= feedback;
                    }
                }
                return output;
            } else {
                // tape
                const output = new Float32Array(data.length + delaySamples * 6);
                output.set(data);
                const delayTone = step.tone;
                const toneAlpha = delayTone < 1 ? (2 * Math.PI * Math.max(200, delayTone * sr / 2) / sr) : 1;
                let tonePrev = 0;
                for (let i = 0; i < data.length; i++) {
                    let fb = feedback;
                    let offset = 0;
                    for (let tap = 0; tap < 6; tap++) {
                        offset += delaySamples;
                        const wobble = Math.floor(Math.sin(2 * Math.PI * 0.5 * i / sr) * 3);
                        const readIdx = i + offset + wobble;
                        if (readIdx >= 0 && readIdx < output.length) {
                            let tapVal = data[i] * fb * wet;
                            if (delayTone < 1) {
                                tonePrev = tonePrev + toneAlpha * (tapVal - tonePrev);
                                tapVal = tonePrev;
                            }
                            output[readIdx] += tapVal;
                        }
                        fb *= feedback;
                    }
                }
                return output;
            }
        }
        case 'reverb': {
            const rtype = step.reverbType;
            const irLen = Math.floor(sr * (0.1 + step.size * 3));
            const damp = step.damp;
            const predelay = Math.floor(step.predelay * sr);
            const impulse = new Float32Array(irLen);
            if (rtype === 'plate') {
                for (let i = 0; i < irLen; i++) { const t = i / irLen; impulse[i] = (Math.random() * 2 - 1) * Math.pow(1 - t, 1.5); }
            } else if (rtype === 'spring') {
                for (let i = 0; i < irLen; i++) { const t = i / irLen; const decay = Math.pow(1 - t, 4); impulse[i] = (Math.sin(2 * Math.PI * 100 * i / sr) * decay + (Math.random() * 2 - 1) * decay * 0.3) * 0.7; }
            } else if (rtype === 'room') {
                for (let i = 0; i < irLen; i++) { const t = i / irLen; impulse[i] = (Math.random() * 2 - 1) * Math.pow(1 - t, 3); }
                [0.01, 0.03, 0.05, 0.07].forEach((rf) => { const idx = Math.floor(rf * sr); if (idx < irLen) impulse[idx] += (Math.random() * 2 - 1) * 0.5; });
            } else if (rtype === 'shimmer') {
                for (let i = 0; i < irLen; i++) { const t = i / irLen; impulse[i] = (Math.random() * 2 - 1) * Math.pow(1 - t, 2) + (impulse[Math.floor(i / 2)] || 0) * 0.5; }
            } else {
                for (let i = 0; i < irLen; i++) { const t = i / irLen; const env = t < 0.1 ? t / 0.1 : Math.pow(1 - (t - 0.1) / 0.9, 2); impulse[i] = (Math.random() * 2 - 1) * env; }
            }
            if (damp > 0) {
                const dampCutoff = Math.max(500, (1 - damp) * sr / 2);
                const dampAlpha = 2 * Math.PI * dampCutoff / sr;
                const da = dampAlpha / (dampAlpha + 1);
                let prev = 0;
                for (let i = 0; i < irLen; i++) { prev = prev + da * (impulse[i] - prev); impulse[i] = prev; }
            }
            let effectiveImpulse = impulse;
            if (predelay > 0) {
                effectiveImpulse = new Float32Array(irLen);
                for (let i = 0; i < irLen; i++) effectiveImpulse[i] = i >= predelay ? impulse[i - predelay] : 0;
            }
            const output = new Float32Array(data.length + irLen - 1);
            for (let i = 0; i < data.length; i++) {
                for (let j = 0; j < irLen; j++) {
                    if (i + j < output.length) output[i + j] += data[i] * effectiveImpulse[j];
                }
            }
            const result = new Float32Array(data.length);
            for (let i = 0; i < data.length; i++) {
                result[i] = data[i] * (1 - step.wet) + (output[i] || 0) * step.wet * 0.5;
            }
            return result;
        }
        case 'paulStretch': {
            const stretchFactor = step.factor;
            const windowSize = Math.floor(sr * 0.1);
            const halfWindow = Math.floor(windowSize / 2);
            const outputLen = Math.floor(data.length * stretchFactor);
            const output = new Float32Array(outputLen);
            const hann = new Float32Array(windowSize);
            for (let i = 0; i < windowSize; i++) hann[i] = 0.5 * (1 - Math.cos(2 * Math.PI * i / (windowSize - 1)));
            let outPos = 0;
            while (outPos < outputLen) {
                const readStart = Math.floor(Math.random() * Math.max(1, data.length - windowSize));
                for (let i = 0; i < windowSize && outPos + i < outputLen; i++) {
                    const srcIdx = readStart + i;
                    if (srcIdx >= 0 && srcIdx < data.length) output[outPos + i] += data[srcIdx] * hann[i];
                }
                outPos += halfWindow;
            }
            for (let i = 0; i < outputLen; i++) output[i] *= 2;
            return output;
        }
        case 'granular': {
            const wet = step.wet;
            const grainSamples = Math.floor(step.grainSize * sr);
            const grainsPerSec = step.density;
            const totalGrains = Math.floor((data.length / sr) * grainsPerSec);
            const pitchVar = step.pitch;
            const spread = step.spread;
            const doReverse = step.grainReverse;
            const outputLen = data.length;
            const output = new Float32Array(outputLen);
            const hann = new Float32Array(grainSamples);
            for (let i = 0; i < grainSamples; i++) hann[i] = 0.5 * (1 - Math.cos(2 * Math.PI * i / (grainSamples - 1)));
            for (let g = 0; g < totalGrains; g++) {
                const seqPos = (g / totalGrains) * outputLen;
                const outStart = Math.floor(seqPos + (Math.random() - 0.5) * spread * outputLen);
                const readStart = Math.floor((1 - spread) * seqPos + spread * Math.random() * Math.max(1, data.length - grainSamples));
                const pitchRatio = pitchVar !== 0 ? Math.pow(2, (Math.random() - 0.5) * pitchVar * 2) : 1;
                const reverse = doReverse && Math.random() < 0.5;
                for (let i = 0; i < grainSamples; i++) {
                    const outIdx = outStart + i;
                    if (outIdx < 0 || outIdx >= outputLen) continue;
                    const srcIdx = readStart + (reverse ? grainSamples - 1 - i : i) * pitchRatio;
                    const i0 = Math.floor(srcIdx);
                    const i1 = Math.min(i0 + 1, data.length - 1);
                    const frac = srcIdx - i0;
                    if (i0 >= 0 && i0 < data.length) {
                        output[outIdx] += (data[i0] * (1 - frac) + data[i1] * frac) * hann[i] * wet;
                    }
                }
            }
            for (let i = 0; i < outputLen; i++) output[i] = output[i] * (1 - wet * 0.5) + data[i] * (1 - wet) * 0.5;
            return output;
        }
        case 'halfSpeed': {
            const newLen = data.length * 2;
            const newData = new Float32Array(newLen);
            for (let i = 0; i < newLen; i++) {
                const srcIdx = i / 2;
                const i0 = Math.floor(srcIdx);
                const i1 = Math.min(i0 + 1, data.length - 1);
                const frac = srcIdx - i0;
                newData[i] = data[i0] * (1 - frac) + data[i1] * frac;
            }
            return newData;
        }
        case 'doubleSpeed': {
            const newLen = Math.floor(data.length / 2);
            const newData = new Float32Array(newLen);
            for (let i = 0; i < newLen; i++) newData[i] = data[i * 2];
            return newData;
        }
        case 'stutter': {
            const grainSize = step.size;
            const output = new Float32Array(data.length);
            let grainIdx = 0, repeatCount = 0;
            for (let i = 0; i < data.length; i++) {
                const grainStart = grainIdx * grainSize;
                const offsetInGrain = i - grainStart;
                if (offsetInGrain >= grainSize) { grainIdx++; repeatCount = 0; }
                const readIdx = grainIdx * grainSize + offsetInGrain;
                if (readIdx < data.length) output[i] = data[readIdx];
                if (offsetInGrain === grainSize - 1) {
                    repeatCount++;
                    if (repeatCount < step.repeats && Math.random() < 0.5) grainIdx--;
                    else repeatCount = 0;
                }
            }
            return output;
        }
        case 'sampleHold': {
            const hold = step.hold;
            const output = new Float32Array(data.length);
            let held = 0;
            for (let i = 0; i < data.length; i++) {
                if (i % hold === 0) held = data[i];
                output[i] = held;
            }
            return output;
        }
        case 'tapeWobble': {
            const depth = step.amount * 0.02;
            const rate = step.rate;
            const output = new Float32Array(data.length);
            for (let i = 0; i < data.length; i++) {
                const wobble = 1 + depth * Math.sin(2 * Math.PI * rate * i / sr);
                const srcIdx = i * wobble;
                const i0 = Math.floor(srcIdx);
                const i1 = Math.min(i0 + 1, data.length - 1);
                const frac = srcIdx - i0;
                if (i0 >= 0 && i0 < data.length) output[i] = data[i0] * (1 - frac) + data[i1] * frac;
            }
            return output;
        }
        case 'freqShift': {
            const shift = step.shift;
            const output = new Float32Array(data.length);
            let phase1 = 0;
            const phaseInc = 2 * Math.PI * shift / sr;
            let prevIn = 0, prevOutR = 0;
            const alpha = 2 * Math.PI * 200 / sr;
            for (let i = 0; i < data.length; i++) {
                phase1 += phaseInc;
                const outR = alpha * (prevOutR + data[i] - prevIn);
                prevIn = data[i]; prevOutR = outR;
                output[i] = data[i] * Math.cos(phase1) - outR * Math.sin(phase1);
            }
            return output;
        }
        case 'fadeIn': {
            const fadeLen = Math.floor(data.length * step.amount);
            for (let i = 0; i < fadeLen && i < data.length; i++) data[i] *= i / fadeLen;
            return data;
        }
        case 'fadeOut': {
            const fadeLen = Math.floor(data.length * step.amount);
            for (let i = 0; i < fadeLen && i < data.length; i++) data[data.length - 1 - i] *= i / fadeLen;
            return data;
        }
        case 'reverse': {
            return data.slice().reverse();
        }
        case 'stereoPan': case 'stereoWidth': case 'autopan': {
            // These are stereo FX — in the new chain they just apply a pan/width
            // For simplicity in mono chain, stereoPan/width/autopan are no-ops on mono data
            // (stereo processing happens post-chain via the step params)
            // We store them for post-processing but don't modify mono data here
            return data;
        }
        default:
            return data;
    }
}

// ============================================================================
// SANITIZE — DC offset removal, click removal, safety limiter, normalize
// ============================================================================

function sanitizeAudio(data, sr, doNormalize) {
    // 1. DC offset removal — subtract mean
    let sum = 0;
    for (let i = 0; i < data.length; i++) sum += data[i];
    const dc = sum / data.length;
    if (Math.abs(dc) > 0.001) {
        for (let i = 0; i < data.length; i++) data[i] -= dc;
    }

    // 2. DC blocker filter (one-pole highpass at ~20Hz) — removes residual DC
    const dcCutoff = 20;
    const dcAlpha = 2 * Math.PI * dcCutoff / sr;
    let dcPrevIn = 0, dcPrevOut = 0;
    for (let i = 0; i < data.length; i++) {
        const out = dcAlpha * (dcPrevOut + data[i] - dcPrevIn);
        dcPrevIn = data[i];
        dcPrevOut = out;
        data[i] = out;
    }

    // 3. Trim silence from start and end — find first/last sample above threshold
    const trimThreshold = 0.005; // -46dB
    let startIdx = 0, endIdx = data.length;
    for (let i = 0; i < data.length; i++) {
        if (Math.abs(data[i]) > trimThreshold) { startIdx = i; break; }
    }
    for (let i = data.length - 1; i >= 0; i--) {
        if (Math.abs(data[i]) > trimThreshold) { endIdx = i + 1; break; }
    }
    // If trimming leaves less than 100 samples, the whole thing is silence
    if (endIdx - startIdx < 100) {
        // Replace with a short audible blip
        data = new Float32Array(Math.floor(sr * 0.05));
        for (let i = 0; i < data.length; i++) {
            const t = i / data.length;
            data[i] = Math.sin(2 * Math.PI * 440 * i / sr) * Math.exp(-t * 30) * 0.5;
        }
        return data;
    }
    // Trim to the active region (keep a small margin)
    const margin = Math.min(Math.floor(sr * 0.005), 50); // 5ms margin
    startIdx = Math.max(0, startIdx - margin);
    endIdx = Math.min(data.length, endIdx + margin);
    if (startIdx > 0 || endIdx < data.length) {
        data = data.slice(startIdx, endIdx);
    }

    // 4. Click removal — tiny fade in/out (1ms) to prevent speaker-damaging clicks
    const fadeSamples = Math.min(Math.floor(sr * 0.001), 64);
    if (fadeSamples > 0 && data.length > fadeSamples * 2) {
        for (let i = 0; i < fadeSamples; i++) {
            const g = i / fadeSamples;
            data[i] *= g;
            data[data.length - 1 - i] *= g;
        }
    }

    // 5. Measure loudness — use the active portion only (ignore trailing silence)
    let peak = 0, rms = 0;
    for (let i = 0; i < data.length; i++) {
        const a = Math.abs(data[i]);
        if (a > peak) peak = a;
        rms += data[i] * data[i];
    }
    rms = Math.sqrt(rms / data.length);

    // If the signal is essentially silence, replace with a short blip
    if (peak < 0.005 || rms < 0.002) {
        data = new Float32Array(Math.floor(sr * 0.1));
        for (let i = 0; i < data.length; i++) {
            const t = i / data.length;
            data[i] = Math.sin(2 * Math.PI * 440 * i / sr) * Math.exp(-t * 15) * 0.5;
        }
        return data;
    }

    // 6. Normalize to safe RMS level — target 0.18 RMS (loud enough to always hear)
    if (doNormalize) {
        const targetRMS = 0.18;
        let g = targetRMS / rms;
        // Cap gain to 50x — if signal is this quiet, it's probably noise
        g = Math.min(g, 50);
        for (let i = 0; i < data.length; i++) data[i] *= g;
    }

    // 7. Re-measure after normalization to verify it's loud enough
    let postRMS = 0;
    for (let i = 0; i < data.length; i++) postRMS += data[i] * data[i];
    postRMS = Math.sqrt(postRMS / data.length);
    // If still too quiet after normalization, force gain up
    if (postRMS < 0.08) {
        const boost = 0.12 / postRMS;
        for (let i = 0; i < data.length; i++) data[i] *= boost;
    }

    // 8. Soft clipper / limiter — prevents any sample from exceeding safe range
    for (let i = 0; i < data.length; i++) {
        const x = data[i];
        if (x > 0.7 || x < -0.7) {
            const sign = x < 0 ? -1 : 1;
            const abs = Math.abs(x);
            data[i] = sign * (0.7 + 0.25 * Math.tanh((abs - 0.7) * 8));
        }
        if (data[i] > 0.99) data[i] = 0.99;
        else if (data[i] < -0.99) data[i] = -0.99;
    }

    return data;
}

// ============================================================================
// PLAYBACK
// ============================================================================

function playAudioBuffer(audioBuffer, canvas, cardId) {
    stopPlayback();
    const ctx = getAudioContext();
    if (ctx.state === 'suspended') ctx.resume();
    const src = ctx.createBufferSource();
    src.buffer = audioBuffer;
    src.connect(ctx.destination);
    src.start(0);
    store.currentSource = src;
    store.playingCardId = cardId;
    store.playBuffer = audioBuffer;
    store.playCanvas = canvas;
    store.playStartTime = ctx.currentTime;
    store.playOffset = 0;
    animatePlayhead();
    src.onended = () => {
        if (store.currentSource === src) {
            stopPlayback();
        }
    };
}

function stopPlayback() {
    if (store.rafId) { cancelAnimationFrame(store.rafId); store.rafId = null; }
    if (store.currentSource) { try { store.currentSource.stop(); } catch (_) {} store.currentSource = null; }
    // Reset playhead to 0 on the card that was playing
    if (store.playCanvas && store.playBuffer) {
        drawWaveformWithPlayhead(store.playCanvas, store.playBuffer, 0);
    }
    store.playingCardId = null;
    store.playBuffer = null;
    store.playCanvas = null;
    qsa('.rfx-card.playing').forEach((el) => el.classList.remove('playing'));
}

function drawWaveformWithPlayhead(canvas, audioBuffer, currentTime) {
    const ctx = canvas.getContext('2d');
    const w = canvas.width, h = canvas.height;
    const imgs = canvas._waveImages;
    if (!imgs) return;
    ctx.fillStyle = window.themeColors ? window.themeColors[0] : '#000';
    ctx.fillRect(0, 0, w, h);
    ctx.drawImage(imgs, 0, 0);
    const dur = audioBuffer.duration;
    if (dur > 0 && currentTime >= 0) {
        const x = Math.floor((currentTime / dur) * w);
        ctx.fillStyle = window.themeColors
            ? `rgba(${parseInt(window.themeColors[9].slice(1,3),16)},${parseInt(window.themeColors[9].slice(3,5),16)},${parseInt(window.themeColors[9].slice(5,7),16)},0.12)`
            : 'rgba(225,225,225,0.12)';
        ctx.fillRect(0, 0, x, h);
        ctx.strokeStyle = window.themeColors ? window.themeColors[14] : '#d2d2d2';
        ctx.lineWidth = 1.5;
        ctx.beginPath();
        ctx.moveTo(x + 0.5, 0);
        ctx.lineTo(x + 0.5, h);
        ctx.stroke();
    }
}

function animatePlayhead() {
    if (store.rafId) cancelAnimationFrame(store.rafId);
    function tick() {
        if (!store.currentSource || !store.playBuffer || !store.playCanvas) return;
        const ctx = getAudioContext();
        const elapsed = store.playOffset + (ctx.currentTime - store.playStartTime);
        if (elapsed >= store.playBuffer.duration) {
            stopPlayback();
            return;
        }
        drawWaveformWithPlayhead(store.playCanvas, store.playBuffer, elapsed);
        store.rafId = requestAnimationFrame(tick);
    }
    store.rafId = requestAnimationFrame(tick);
}

// ============================================================================
// CARD GRID
// ============================================================================

function createCard(params, fxSettings, presetName, noteName, targetDuration, audioBuffer) {
    const id = ++store.cardCounter;
    if (!audioBuffer) return null;
    const randomTag = randomName();
    const cardData = { id, params, audioBuffer, fxSettings, presetName, noteName, randomTag };

    const card = document.createElement('div');
    card.className = 'rfx-card';
    card.dataset.cardId = id;

    const header = document.createElement('div');
    header.className = 'rfx-card-header';

    const title = document.createElement('span');
    title.className = 'rfx-card-title';
    title.draggable = true;
    title.textContent = randomTag;
    title.title = 'Drag to bucket';

    const controls = document.createElement('div');
    controls.className = 'rfx-card-controls';

    const dlBtn = document.createElement('button');
    dlBtn.className = 'rfx-btn';
    dlBtn.textContent = '\u2193';
    dlBtn.title = 'Download WAV';

    const bucketBtn = document.createElement('button');
    bucketBtn.className = 'rfx-btn';
    bucketBtn.textContent = '\u2192';
    bucketBtn.title = 'Send to bucket';

    const delBtn = document.createElement('button');
    delBtn.className = 'rfx-btn';
    delBtn.textContent = '\u2715';
    delBtn.title = 'Delete';

    controls.appendChild(dlBtn);
    controls.appendChild(bucketBtn);
    controls.appendChild(delBtn);
    header.appendChild(title);
    header.appendChild(controls);

    const waveContainer = document.createElement('div');
    waveContainer.className = 'rfx-card-waveform';
    waveContainer.draggable = true;
    const canvas = document.createElement('canvas');
    waveContainer.appendChild(canvas);

    const info = document.createElement('div');
    info.className = 'rfx-card-info';
    const durSpan = document.createElement('span');
    durSpan.textContent = formatDuration(audioBuffer.duration);
    const srSpan = document.createElement('span');
    srSpan.textContent = `${audioBuffer.sampleRate}Hz`;
    const chSpan = document.createElement('span');
    chSpan.textContent = audioBuffer.numberOfChannels > 1 ? 'stereo' : 'mono';
    info.appendChild(durSpan);
    info.appendChild(srSpan);
    info.appendChild(chSpan);

    card.appendChild(header);
    card.appendChild(waveContainer);
    card.appendChild(info);

    requestAnimationFrame(() => renderCardWaveform(canvas, audioBuffer));

    canvas.addEventListener('click', () => {
        if (store.playingCardId === id) {
            stopPlayback();
        } else {
            playAudioBuffer(audioBuffer, canvas, id);
            card.classList.add('playing');
        }
    });

    dlBtn.addEventListener('click', () => downloadCard(cardData));
    bucketBtn.addEventListener('click', () => sendCardToBucket(cardData));
    delBtn.addEventListener('click', () => {
        if (store.playingCardId === id) stopPlayback();
        card.remove();
        store.cards = store.cards.filter((c) => c.id !== id);
        updateCardCount();
    });

    [title, waveContainer].forEach((el) => {
        el.addEventListener('dragstart', (e) => {
            e.dataTransfer.effectAllowed = 'copy';
            e.dataTransfer.setData('application/x-randfxr-card', String(id));
            e.dataTransfer.setData('text/plain', `${randomTag}.wav`);
            card.classList.add('dragging');
        });
        el.addEventListener('dragend', () => card.classList.remove('dragging'));
    });

    cardData.element = card;
    cardData.canvas = canvas;
    return cardData;
}

async function renderCardWaveform(canvas, audioBuffer) {
    const container = canvas.parentElement;
    let w = container.clientWidth;
    let h = container.clientHeight;
    if (w < 2 || h < 2) { requestAnimationFrame(() => renderCardWaveform(canvas, audioBuffer)); return; }
    canvas.width = w;
    canvas.height = h;
    try {
        const waveCanvas = await renderWaveform(audioBuffer, { channel: 0, startSample: 0, endSample: audioBuffer.length }, w, h, 1);
        canvas._waveImages = waveCanvas;
        const ctx = canvas.getContext('2d');
        ctx.fillStyle = window.themeColors ? window.themeColors[0] : '#000';
        ctx.fillRect(0, 0, w, h);
        ctx.drawImage(waveCanvas, 0, 0);
    } catch (err) {
    }
}

function addCardToGrid(cardData) {
    const grid = qs('#rfx-card-grid');
    // Remove empty placeholder if present
    const empty = grid.querySelector('.rfx-grid-empty');
    if (empty) empty.remove();
    // Latest on top (prepend)
    grid.insertBefore(cardData.element, grid.firstChild);
    store.cards.unshift(cardData);
    updateCardCount();
}

function updateCardCount() {
    const el = qs('#rfx-card-count');
    if (el) el.textContent = store.cards.length;
}

// ============================================================================
// BUCKET INTEGRATION
// ============================================================================

// ============================================================================
// RANDOM NAME GENERATOR
// ============================================================================

const NAME_SYLLABLES = [
    'za','xu','vi','ro','me','ka','tu','no','pi','se','lu','dra','mor','kir',
    'vel','syn','gor','tha','nex','quo','zyx','wun','flo','kri','dun','per',
    'ax','om','ur','il','eb','oc','yd','uv','ik','en','ob','ar','un','el',
    'tor','gax','vyn','sho','bru','cyl','dax','fex','glo','hiv','jor','kly',
    'mox','nur','pik','ryv','sor','tro','vex','wol','xar','yot','zil','bri',
];

function randomName() {
    const numSyllables = 2 + Math.floor(Math.random() * 3); // 2-4 syllables
    let name = '';
    for (let i = 0; i < numSyllables; i++) {
        name += NAME_SYLLABLES[Math.floor(Math.random() * NAME_SYLLABLES.length)];
    }
    // Capitalize first letter
    return name.charAt(0).toUpperCase() + name.slice(1);
}

function cardFilename(cardData) {
    return (cardData.randomTag || 'sfx') + '.wav';
}

function sendCardToBucket(cardData) {
    if (!randfxrBucket.bucket) { alertmessage('Bucket not ready'); return; }
    const wav = audioBufferToWav(cardData.audioBuffer);
    randfxrBucket.bucket.add({
        source: 'randfxr',
        name: cardFilename(cardData),
        wavLike: new Uint8Array(wav),
        fileType: 'wav',
    });
}

function downloadCard(cardData) {
    const wav = audioBufferToWav(cardData.audioBuffer);
    const blob = new Blob([wav], { type: 'audio/wav' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = cardFilename(cardData);
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
}

function setupCardDragToBucket() {
    const bucketPanel = document.querySelector('.xtrc-bucket-panel');
    if (!bucketPanel) return;
    bucketPanel.addEventListener('dragover', (e) => {
        if (e.dataTransfer.types.includes('application/x-randfxr-card')) {
            e.preventDefault();
            e.dataTransfer.dropEffect = 'copy';
        }
    }, true);
    bucketPanel.addEventListener('drop', (e) => {
        const cardId = e.dataTransfer.getData('application/x-randfxr-card');
        if (cardId) {
            const cardData = store.cards.find((c) => c.id === parseInt(cardId));
            if (cardData) {
                e.preventDefault();
                e.stopPropagation();
                sendCardToBucket(cardData);
            }
        }
    }, true);
}

// ============================================================================
// GENERATE ONE — single sound per click
// ============================================================================

let isGenerating = false;

const SPINNER_FRAMES = ['/|\-', '-/|\\', '\\-/|', '|\\-/'];
let spinnerInterval = null;

function startSpinner() {
    const btn = qs('#rfx-generate-btn');
    if (!btn) return;
    let frame = 0;
    btn.textContent = SPINNER_FRAMES[0];
    spinnerInterval = setInterval(() => {
        frame = (frame + 1) % SPINNER_FRAMES.length;
        btn.textContent = SPINNER_FRAMES[frame];
    }, 80);
}

function stopSpinner() {
    if (spinnerInterval) { clearInterval(spinnerInterval); spinnerInterval = null; }
    const btn = qs('#rfx-generate-btn');
    if (btn) btn.textContent = 'GENERATE';
}

// ---- Progress bar ----
function showProgressBar() {
    const bar = qs('#rfx-progress');
    if (bar) bar.hidden = false;
    updateProgressBar(0, 1, 'starting…');
}

function hideProgressBar() {
    const bar = qs('#rfx-progress');
    if (!bar) return;
    const fill = bar.querySelector('.rfx-progress-fill');
    const labelEl = bar.querySelector('.rfx-progress-label');
    if (fill) fill.style.width = '0%';
    if (labelEl) labelEl.textContent = '';
    bar.hidden = true;
}

function updateProgressBar(step, total, label) {
    const bar = qs('#rfx-progress');
    if (!bar) return;
    const fill = bar.querySelector('.rfx-progress-fill');
    const labelEl = bar.querySelector('.rfx-progress-label');
    const pct = total > 0 ? Math.min(100, Math.round((step / total) * 100)) : 0;
    if (fill) fill.style.width = pct + '%';
    if (labelEl) labelEl.textContent = label || '';
}

function generateOne() {
    if (isGenerating) return;
    isGenerating = true;

    const genBtn = qs('#rfx-generate-btn');
    if (genBtn) genBtn.disabled = true;
    startSpinner();
    showProgressBar();

    generateWithRetry((p) => {
        updateProgressBar(p.step, p.total, p.label + (p.attempt > 1 ? ' (retry ' + (p.attempt - 1) + ')' : ''));
    }).then(({ audioBuffer, presetName, noteName }) => {
        if (audioBuffer && audioBuffer.length > 0) {
            const cardData = createCard(null, null, presetName, noteName, audioBuffer.duration, audioBuffer);
            if (cardData) addCardToGrid(cardData);
        }
    }).catch((err) => {
    }).finally(() => {
        isGenerating = false;
        if (genBtn) genBtn.disabled = false;
        stopSpinner();
        hideProgressBar();
    });
}

function clearGrid() {
    stopPlayback();
    const grid = qs('#rfx-card-grid');
    if (grid) grid.innerHTML = '<div class="rfx-grid-empty">Click GENERATE to create a random sound.</div>';
    store.cards = [];
    updateCardCount();
}

// ============================================================================
// HELPERS
// ============================================================================

function formatDuration(duration) {
    const ms = Math.floor(duration * 1000);
    if (ms < 1000) return `${ms}ms`;
    return `${(duration).toFixed(2)}s`;
}

// ============================================================================
// INIT
// ============================================================================

const randfxrBucket = initLegacyBucket({
    appName: 'randfxr',
});

function init() {
    const genBtn = qs('#rfx-generate-btn');
    if (genBtn) genBtn.addEventListener('click', generateOne);

    const clearBtn = qs('#rfx-clear-btn');
    if (clearBtn) clearBtn.addEventListener('click', clearGrid);

    setupCardDragToBucket();

    let resizeTimer = null;
    window.addEventListener('resize', () => {
        clearTimeout(resizeTimer);
        resizeTimer = setTimeout(() => {
            store.cards.forEach((c) => {
                if (c.canvas) renderCardWaveform(c.canvas, c.audioBuffer);
            });
        }, 200);
    });
}

document.addEventListener('DOMContentLoaded', init);
